// Export of the not-yet-matched rows of a matching screen (card / bank) as an Xtram PDF or an Excel file.
// A report is a list of sections, each a table: { title, headers, rows: [[...values]], amountIndex }.
import * as XLSX from 'xlsx-js-style';
import { applyWorkbookRtl, AMOUNT_NUMBER_FORMAT, REPORT_STYLES } from './ExcelReport';
import { beautifyNumber, parseBeautifiedNumber } from './FormatNumber';
import EnumsService from '../Services/enums.service';
import { XTRAM_LOGOS } from '../assests/branding/xtram/logos';
import { AGING_REPORT_COLORS as C } from './receivablesAgingExport';

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const escapeHtml = (value) =>
    String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

const formatDisplayDate = (d) => {
    if (!d) return '';
    const date = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(date.getTime())) return '';
    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
};

const safeFileName = (value) => String(value || 'Client').replace(/[\\/:*?"<>|]/g, '_');

/** Amounts arrive as numbers or as formatted strings ("1,234.50", "(12.00)"). */
export const toAmount = (value) => {
    if (typeof value === 'number') return value;
    const n = parseBeautifiedNumber(value);
    return Number.isFinite(n) ? n : 0;
};

const sectionTotal = (section, column = section.amountIndex) =>
    round2((section.rows || []).reduce((sum, row) => sum + toAmount(row[column]), 0));

/** Columns that hold figures: the amount, plus any extra a section declares (`numericIndexes`). */
const numericColumnsOf = (section) =>
    new Set([section.amountIndex, ...(section.numericIndexes || [])].filter((c) => c >= 0));

/** How a VAT percentage reads on a report: the accounting-tax rate has a name, the rest are percentages. */
export const vatLabel = (vat, t = (k, d) => d || k) => {
    if (vat === null || vat === undefined || vat === '') return '';
    const value = Number(vat);
    if (!Number.isFinite(value)) return '';
    return value === 16 ? t('AccountingTax', 'מס תשומות') : `${value}%`;
};

/**
 * One side of a card-matching report: the columns an accountant works the open items against.
 * `cardLabelOf` turns an account id into the label to print for it, `sortCodeLabelOf` a sort code.
 */
export const buildUnmatchedSection = ({
    title,
    rows = [],
    cardLabelOf = (id) => (id ?? ''),
    sortCodeLabelOf = (id) => (id ?? ''),
    t = (k, d) => d || k,
}) => ({
    title,
    headers: [
        t('Code ID'), t('Debit Account'), t('Credit Account'), t('Document Date'),
        t('Amount'), t('VAT'), t('Reference'), t('Description'),
    ],
    // Accounts, sort codes and dates are fixed-width, the description takes whatever is left.
    weights: [1.6, 1.3, 1.3, 1, 1, 0.6, 1, 1.8],
    amountIndex: 4,
    rows: rows.map((row) => [
        sortCodeLabelOf(row.sortCodeId),
        cardLabelOf(row.debitAccount),
        cardLabelOf(row.creditAccount),
        row.documentDate,
        row.amount1,
        vatLabel(row.vat, t),
        row.reference ?? '',
        row.description ?? '',
    ]),
});

/**
 * The first page of a many-card report: one line per card, so the reader sees which cards are
 * open and by how much before reading any of them. `cards` are
 * `{ label, sections: [debitSection, creditSection] }`.
 */
export const buildUnmatchedCardsSummary = ({ cards = [], sideLabels, t = (k, d) => d || k }) => ({
    title: t('Summary', 'סיכום'),
    headers: [
        t('AccountCard', 'כרטיס חשבון'), t('OpenItems', 'פריטים פתוחים'),
        ...(sideLabels || [t('Debit', 'חובה'), t('Credit', 'זכות')]),
        t('Difference', 'הפרש'),
    ],
    weights: [3, 1, 1.3, 1.3, 1.3],
    amountIndex: 2,
    numericIndexes: [3, 4],
    rows: cards.map((card) => {
        const [debit, credit] = card.sections;
        const debitTotal = sectionTotal(debit);
        const creditTotal = sectionTotal(credit);
        return [
            card.label,
            // A count, not money: left as text so it is not printed with decimals.
            String((debit.rows?.length || 0) + (credit.rows?.length || 0)),
            debitTotal,
            creditTotal,
            round2(debitTotal - creditTotal),
        ];
    }),
});

/**
 * Every open item of every card on one sheet, with the card and the side it sits on as the
 * leading columns — one sheet that can be filtered beats a sheet per card.
 */
export const buildUnmatchedCardsFlatSection = ({ cards = [], t = (k, d) => d || k }) => {
    const [template] = cards.flatMap((card) => card.sections);
    const headers = [t('AccountCard', 'כרטיס חשבון'), t('Side', 'צד'), ...(template?.headers || [])];
    const leadingColumns = 2;
    return {
        title: t('UnmatchedReport'),
        headers,
        amountIndex: (template?.amountIndex ?? 0) + leadingColumns,
        rows: cards.flatMap((card) =>
            card.sections.flatMap((section) =>
                (section.rows || []).map((row) => [card.label, section.title, ...row])
            )
        ),
    };
};

const periodLabel = (startDate, endDate) =>
    startDate || endDate ? `${formatDisplayDate(startDate)} – ${formatDisplayDate(endDate)}` : '';

const assetToDataUrl = async (assetUrl) => {
    try {
        const res = await fetch(assetUrl);
        if (!res.ok) return '';
        const blob = await res.blob();
        return await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(String(reader.result || ''));
            reader.onerror = () => resolve('');
            reader.readAsDataURL(blob);
        });
    } catch {
        return '';
    }
};

export const downloadUnmatchedExcel = ({
    sections,
    title,
    clientName,
    clientCase,
    cardLabel,
    startDate,
    endDate,
    fileSuffix = 'Unmatched',
    t = (k, d) => d || k,
}) => {
    const wb = XLSX.utils.book_new();
    const titleLines = [
        `${clientCase || ''}    ${clientName || ''}`.trim(),
        title,
        [cardLabel, periodLabel(startDate, endDate)].filter(Boolean).join('    '),
    ];

    (sections || []).forEach((section, sectionIdx) => {
        const colCount = section.headers.length;
        const numeric = numericColumnsOf(section);
        const aoa = titleLines.map((line) => [line]);
        aoa.push([section.title]);
        aoa.push([]);
        const headerRowIndex = aoa.length;
        aoa.push(section.headers);

        const dataRows = (section.rows || []).map((row) =>
            row.map((v, c) => (numeric.has(c) ? round2(toAmount(v)) : v ?? ''))
        );
        const totalRow = Array(colCount).fill('');
        totalRow[0] = `${t('Total', 'סה"כ')} (${dataRows.length})`;
        numeric.forEach((c) => { totalRow[c] = sectionTotal(section, c); });
        dataRows.push(totalRow);
        dataRows.forEach((r) => aoa.push(r));

        const ws = XLSX.utils.aoa_to_sheet(aoa);
        ws['!merges'] = [...titleLines, section.title].map((_, i) => ({
            s: { r: i, c: 0 },
            e: { r: i, c: Math.max(colCount - 1, 0) },
        }));
        ws['!cols'] = section.headers.map((_, c) => ({ wch: numeric.has(c) ? 16 : c === 0 ? 36 : 20 }));
        ws['!views'] = [{ RTL: true }];

        const titleStyles = [REPORT_STYLES.clientLine, REPORT_STYLES.reportTitle, REPORT_STYLES.subTitle, REPORT_STYLES.subTitle];
        titleStyles.forEach((style, i) => {
            const addr = XLSX.utils.encode_cell({ r: i, c: 0 });
            if (ws[addr]) ws[addr].s = style;
        });

        for (let c = 0; c < colCount; c += 1) {
            const addr = XLSX.utils.encode_cell({ r: headerRowIndex, c });
            if (!ws[addr]) ws[addr] = { t: 's', v: '' };
            ws[addr].s = REPORT_STYLES.header;
        }

        dataRows.forEach((_, rowIdx) => {
            const r = headerRowIndex + 1 + rowIdx;
            const isTotal = rowIdx === dataRows.length - 1;
            const isStripe = !isTotal && rowIdx % 2 === 1;
            for (let c = 0; c < colCount; c += 1) {
                const addr = XLSX.utils.encode_cell({ r, c });
                if (!ws[addr]) ws[addr] = { t: 's', v: '' };
                const cell = ws[addr];
                const isNum = numeric.has(c);
                if (isTotal) cell.s = isNum ? REPORT_STYLES.totalNum : REPORT_STYLES.totalText;
                else if (isStripe) cell.s = isNum ? REPORT_STYLES.stripeNum : REPORT_STYLES.stripeText;
                else cell.s = isNum ? REPORT_STYLES.dataNum : REPORT_STYLES.dataText;
                if (isNum && typeof cell.v === 'number') cell.z = AMOUNT_NUMBER_FORMAT;
            }
        });

        const sheetName = String(section.title || `Sheet${sectionIdx + 1}`).replace(/[\\/?*[\]:]/g, ' ').slice(0, 31);
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    applyWorkbookRtl(wb);
    XLSX.writeFile(wb, `${safeFileName(clientName)}_${fileSuffix}.xlsx`);
};

// The two sides sit next to each other in one table: a flex/float layout would not
// fragment cleanly across pages, while table rows do and the header repeats.
const SIDE_GAP_PCT = 2.5;

/**
 * Width per column, as a percentage of the page. A section can weight its own columns
 * (`weights`), which is what keeps a wide column set readable; otherwise the first column
 * and the amount get the extra room.
 */
const columnWidths = (section, share = (100 - SIDE_GAP_PCT) / 2) => {
    const weights = section.weights?.length === section.headers.length
        ? section.weights
        : section.headers.map((_, c) => (c === 0 ? 2.3 : c === section.amountIndex ? 1.3 : 1));
    const weightSum = weights.reduce((sum, w) => sum + w, 0) || 1;
    return weights.map((w) => round2((w / weightSum) * share));
};

export const buildUnmatchedPdfHtml = ({
    sections,
    groups,        // [{ title, sections: [debitSection, creditSection] }] — one card each
    summary,       // section printed on its own first page
    title,
    clientName,
    clientCase,
    cardLabel,
    startDate,
    endDate,
    logoDataUrl,
    t = (k, d) => d || k,
}) => {
    const dataCells = (section, row) => {
        const numeric = numericColumnsOf(section);
        return section.headers
            .map((_, c) => {
                // One side usually runs longer than the other; its leftover depth stays ruled
                // but unshaded so it reads as empty rather than as data.
                if (!row) return '<td class="pad"></td>';
                const value = row[c];
                return numeric.has(c)
                    ? `<td class="num">${escapeHtml(beautifyNumber(toAmount(value)))}</td>`
                    : `<td class="txt">${escapeHtml(value)}</td>`;
            })
            .join('');
    };

    const totalCells = (section) => {
        const numeric = numericColumnsOf(section);
        return section.headers
            .map((_, c) => {
                if (c === 0) return `<td class="txt">${escapeHtml(`${t('Total', 'סה"כ')} (${(section.rows || []).length})`)}</td>`;
                if (numeric.has(c)) return `<td class="num">${escapeHtml(beautifyNumber(sectionTotal(section, c)))}</td>`;
                return '<td></td>';
            })
            .join('');
    };

    /** One ledger: the first section takes the right-hand half, the second the left, as on screen. */
    const splitTableHtml = (rightSection, leftSection) => {
        const rightRows = rightSection.rows || [];
        const leftRows = leftSection.rows || [];
        const rowCount = Math.max(rightRows.length, leftRows.length);
        const cols = [
            ...columnWidths(rightSection).map((w) => `<col style="width:${w}%" />`),
            `<col style="width:${SIDE_GAP_PCT}%" />`,
            ...columnWidths(leftSection).map((w) => `<col style="width:${w}%" />`),
        ].join('');
        const headerCells = (section) => section.headers.map((h) => `<th>${escapeHtml(h)}</th>`).join('');
        const body = Array.from({ length: rowCount }, (_, idx) => {
            const stripe = idx % 2 === 1 ? 'stripe' : 'plain';
            return `<tr class="${stripe}">${dataCells(rightSection, rightRows[idx])}<td class="gap"></td>${dataCells(leftSection, leftRows[idx])}</tr>`;
        }).join('');

        return `
  <table class="split">
    <colgroup>${cols}</colgroup>
    <thead>
      <tr class="side-titles">
        <th colspan="${rightSection.headers.length}" class="side-title debit">${escapeHtml(rightSection.title)}</th>
        <th class="gap"></th>
        <th colspan="${leftSection.headers.length}" class="side-title credit">${escapeHtml(leftSection.title)}</th>
      </tr>
      <tr>${headerCells(rightSection)}<th class="gap"></th>${headerCells(leftSection)}</tr>
    </thead>
    <tbody>
      ${body}
      <tr class="total">${totalCells(rightSection)}<td class="gap"></td>${totalCells(leftSection)}</tr>
    </tbody>
  </table>`;
    };

    const totalsStripHtml = (rightSection, leftSection) => {
        const rightTotal = sectionTotal(rightSection);
        const leftTotal = sectionTotal(leftSection);
        const difference = round2(rightTotal - leftTotal);
        const cell = (label, value, cls = '') =>
            `<div class="strip-cell ${cls}"><span class="strip-label">${escapeHtml(label)}</span><span class="strip-value">${escapeHtml(beautifyNumber(value))}</span></div>`;
        return `
  <div class="totals-strip">
    ${cell(rightSection.title, rightTotal)}
    ${cell(leftSection.title, leftTotal)}
    ${cell(t('Difference', 'הפרש'), difference, Math.abs(difference) < 0.01 ? 'ok' : 'warn')}
  </div>`;
    };

    /** One table across the page, for the summary and for anything that is not a two-sided card. */
    const sectionHtml = (section) => {
        const rows = section.rows || [];
        const cols = columnWidths(section, 100).map((w) => `<col style="width:${w}%" />`).join('');
        const body = rows
            .map((row, idx) => `<tr class="${idx % 2 === 1 ? 'stripe' : 'plain'}">${dataCells(section, row)}</tr>`)
            .join('');
        return `
  <h2>${escapeHtml(section.title)}</h2>
  <table>
    <colgroup>${cols}</colgroup>
    <thead><tr>${section.headers.map((h) => `<th>${escapeHtml(h)}</th>`).join('')}</tr></thead>
    <tbody>${body}<tr class="total">${totalCells(section)}</tr></tbody>
  </table>`;
    };

    /** One card: its name, its totals and its two sides. */
    const groupHtml = (group, index) => {
        const [right, left] = group.sections || [];
        const tables = right && left
            ? `${totalsStripHtml(right, left)}${splitTableHtml(right, left)}`
            : (group.sections || []).map(sectionHtml).join('');
        // Each card starts on a page of its own, so a card is never split across two pages
        // by something that happened on the card before it.
        return `
  <section class="${index === 0 ? 'card-block' : 'card-block page-break'}">
    ${group.title ? `<h2 class="card-heading">${escapeHtml(group.title)}</h2>` : ''}
    ${tables}
  </section>`;
    };

    const groupList = groups?.length
        ? groups
        : (sections?.length ? [{ sections }] : []);
    const tablesHtml = [
        summary ? `<section class="card-block">${sectionHtml(summary)}</section>` : '',
        ...groupList.map((group, index) => groupHtml(group, summary ? index + 1 : index)),
    ].join('');

    const now = new Date();
    const printedAt = `${now.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })} ${formatDisplayDate(now)}`;

    return `<!DOCTYPE html>
<html lang="he" dir="rtl">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    /* Landscape: the two sides have to fit next to each other.
       The server renders with PreferCSSPageSize, so this is what decides the page. */
    @page { size: A4 landscape; margin: 8mm 6mm; }
    * { box-sizing: border-box; }
    body {
      font-family: Arial, Helvetica, "Segoe UI", sans-serif;
      direction: rtl;
      color: ${C.text};
      margin: 0;
      padding: 6px;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .top-bar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; font-size: 11px; }
    .top-bar img { height: 28px; }
    .center-block { text-align: center; flex: 1; }
    h1 { margin: 2px 0 4px; font-size: 18px; color: ${C.primary}; font-weight: 700; }
    h2 { margin: 14px 0 6px; font-size: 13px; color: ${C.primaryDark}; }
    .meta { font-size: 11px; margin-bottom: 2px; font-weight: 600; }
    table { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 9px; }
    thead { display: table-header-group; }
    tr { page-break-inside: avoid; }
    th, td { border: 1px solid ${C.border}; padding: 4px 3px; vertical-align: middle; word-wrap: break-word; }
    th { background: ${C.primary} !important; color: ${C.headerFont} !important; font-weight: 700; text-align: center; }
    td.txt { text-align: center; }
    td.num { text-align: center; white-space: nowrap; font-variant-numeric: tabular-nums; font-weight: 600; }
    tr.plain td { background: ${C.plain} !important; }
    tr.stripe td { background: ${C.stripe} !important; }
    tr.total td { font-weight: 700; background: ${C.totalFill} !important; color: ${C.primaryDark} !important; }

    /* The split ledger: debit half on the right, credit half on the left. */
    table.split { font-size: 8.5px; }
    table.split th.side-title { font-size: 11px; padding: 5px 3px; letter-spacing: 0.3px; }
    table.split th.side-title.credit { background: ${C.primaryDark} !important; }
    table.split .gap { border: none !important; background: #ffffff !important; padding: 0; }
    table.split td.pad { background: ${C.plain} !important; border-color: #E6EAF5 !important; }

    .totals-strip { display: flex; gap: 6px; margin: 0 0 8px; }
    .strip-cell {
      flex: 1 1 0;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 6px;
      padding: 4px 8px;
      border: 1px solid ${C.border};
      border-radius: 4px;
      background: ${C.totalFill} !important;
    }
    .strip-label { font-size: 9px; font-weight: 600; color: ${C.primaryDark}; }
    .strip-value { font-size: 11px; font-weight: 700; font-variant-numeric: tabular-nums; }
    .strip-cell.warn .strip-value { color: #D32F2F; }

    .card-block { margin-bottom: 10px; }
    .page-break { page-break-before: always; }
    h2.card-heading {
      margin: 0 0 6px;
      padding: 4px 8px;
      font-size: 12px;
      color: ${C.headerFont};
      background: ${C.primaryDark} !important;
      border-radius: 4px;
    }
  </style>
</head>
<body>
  <div class="top-bar">
    <div>${logoDataUrl ? `<img src="${logoDataUrl}" alt="Xtram" />` : ''}</div>
    <div class="center-block">
      <div class="meta">${escapeHtml(`${clientName || ''} ${clientCase ? `(${clientCase})` : ''}`.trim())}</div>
      <h1>${escapeHtml(title)}</h1>
      <div class="meta">${escapeHtml(cardLabel || '')}</div>
      <div class="meta">${escapeHtml(periodLabel(startDate, endDate))}</div>
    </div>
    <div>${escapeHtml(printedAt)}</div>
  </div>
  ${tablesHtml}
</body>
</html>`;
};

export const downloadUnmatchedPdf = async ({ token, clientCase, clientName, fileSuffix = 'Unmatched', ...report }) => {
    if (!token) throw new Error('Missing auth token');
    if (!clientCase) throw new Error('Missing case id');

    const logoDataUrl = await assetToDataUrl(XTRAM_LOGOS.wordmarkColorPng);
    const html = buildUnmatchedPdfHtml({ ...report, clientCase, clientName, logoDataUrl });

    const res = await EnumsService.GeneratePdfFromHtml(html, clientCase, token);
    if (!res?.ok) {
        const errText = await res?.text?.().catch(() => '');
        throw new Error(errText || 'PDF generation failed');
    }

    let blob;
    if ((res.headers.get('content-type') || '').includes('application/json')) {
        const data = await res.json();
        const pdfBase64 = data.data?.PdfBase64 || data.data?.pdfBase64 || data.PdfBase64;
        if (!pdfBase64) throw new Error('No PDF base64 in response');
        const binary = atob(pdfBase64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
        blob = new Blob([bytes], { type: 'application/pdf' });
    } else {
        blob = await res.blob();
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safeFileName(clientName)}-${clientCase}_${fileSuffix}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};
